import { Formik } from 'formik';
import * as Yup from 'yup';
import {
  GetDetailsResponseType,
  PriorPolicyResponseType,
  transformAppDetails,
} from 'QuoteApp/services/endpoints/GetDetails';
import { PriorPolicyChallengeFormValuesType } from 'QuoteApp/services/endpoints/PostChallengePriorPolicy';
import React, { useMemo, useState } from 'react';
import { ReportDetailRow } from 'QuoteApp/components/ReportDetails/ReportDetailRow';
import { ChallengeForm } from 'QuoteApp/components/ReportDetails/ChallengeForm';
import PriorPolicyChallengeForm, {
  parseISODateForDisplay,
} from 'QuoteApp/components/ReportDetails/PriorPolicyChallengeForm/PriorPolicyChallengeForm';
import { useLazyQuoteApi } from 'Shared/services/Api';
import { transformChallengePriorPolicyRequest } from 'QuoteApp/services/endpoints/PostChallengePriorPolicy/request';
import { useAppContext } from 'QuoteApp/services/providers';
import { getErrorMessage } from 'QuoteApp/utils';
import { useToast } from 'Shared/components';
import {
  getReportMessages,
  ReportChangeMessageType,
  ReportMessages,
} from 'QuoteApp/screens/Reports';
import { useEdit } from 'QuoteApp/utils/contexts';
import { abbreviateNumber, validateCoverageLimitWithK } from 'Shared/utils';
import { isServerDate, parseDateSlashFormatShort } from 'Shared/utils/date';
import styles from './styles.module.css';

// Validation helper function using shared utility
const validateCoverageLimitYup = (
  value: string,
  type: 'BI' | 'CSL' | 'PIP'
): boolean => {
  const validation = validateCoverageLimitWithK(value, type);
  return validation.isValid;
};

type PriorPolicyFormPropsType = {
  canChallenge: boolean;
  priorPolicy: PriorPolicyResponseType['attributes'];
  commands: PriorPolicyResponseType['commands'];
  driverId: string;
  onCancelAdd: () => void;
};

const getChallengePriorPolicyInitialFormValues = (
  priorPolicy: PriorPolicyResponseType['attributes'],
  identifier: string
): PriorPolicyChallengeFormValuesType => {
  return {
    [identifier]: {
      challengeType: undefined,
      carrierName: priorPolicy.carrierName,
      startDate: parseISODateForDisplay(priorPolicy.startDate),
      endDate: parseISODateForDisplay(priorPolicy.endDate),
      amBestNumber: priorPolicy.amBestNumber,
      state: priorPolicy.state?.split('-')[1],
      biLimit: (() => {
        const coverage = priorPolicy.coverages.find(
          (coverage) => coverage.coverageType === 'bodilyInjury'
        );
        return coverage &&
          (coverage.limitPerPerson > 0 || coverage.limitPerAccident > 0)
          ? `${coverage.limitPerPerson.toLocaleString()}/${coverage.limitPerAccident.toLocaleString()}`
          : '';
      })(),
      cslLimit: (() => {
        const coverage = priorPolicy.coverages.find(
          (coverage) => coverage.coverageType === 'combinedSingleLimit'
        );
        return coverage && coverage.limitPerPerson > 0
          ? `$${abbreviateNumber(coverage.limitPerPerson)}`
          : '';
      })(),
      pipLimit: (() => {
        const coverage = priorPolicy.coverages.find(
          (coverage) => coverage.coverageType === 'personalInjuryProtection'
        );
        return coverage && coverage.limitPerPerson > 0
          ? `$${abbreviateNumber(coverage.limitPerPerson)}`
          : '';
      })(),
      reasonText: '',
      challengeReason: '',
      policyNumber: priorPolicy.policyNumber,
      limits: '',
    },
  };
};

export const getValidationSchema = (identifier: string) => {
  return Yup.object().shape({
    [identifier]: Yup.object().shape({
      policyNumber: Yup.string().required('Required'),
      challengeType: Yup.string(),
      carrierName: Yup.string().required('Required'),
      startDate: Yup.string().required('Required'),
      endDate: Yup.string()
        .required('Required')
        .test(
          'endDate-after-startDate',
          'Must be after Effective date',
          function (value) {
            const { startDate } = this.parent;
            if (!value || !startDate) return true;

            // Parse dates for comparison using consistent business logic
            const parseDate = (dateStr: string): Date | null => {
              // Handle ISO format dates using robust detection
              if (isServerDate(dateStr)) {
                return new Date(dateStr);
              }
              // Handle MM/DD/YY format using shared utility for consistency
              if (dateStr.includes('/')) {
                return parseDateSlashFormatShort(dateStr);
              }
              // Fallback for other formats
              try {
                return new Date(dateStr);
              } catch {
                return null;
              }
            };

            try {
              const startDateObj = parseDate(startDate);
              const endDateObj = parseDate(value);

              // If either date is invalid, let other validations handle it
              if (!startDateObj || !endDateObj) {
                return true;
              }

              return endDateObj >= startDateObj;
            } catch (error) {
              return true; // Let other validations handle invalid date formats
            }
          }
        ),
      amBestNumber: Yup.number().min(0).max(9999999999).nullable(),
      state: Yup.string().required('Required'),
      biLimit: Yup.string()
        .nullable()
        .test(
          'bi-format',
          'BI must have format: amount/amount (e.g., 100K/300K)',
          function (value) {
            return validateCoverageLimitYup(value || '', 'BI');
          }
        ),
      cslLimit: Yup.string()
        .nullable()
        .test(
          'csl-format',
          'CSL must be a single amount (e.g., 100K)',
          function (value) {
            return validateCoverageLimitYup(value || '', 'CSL');
          }
        ),
      pipLimit: Yup.string()
        .nullable()
        .test(
          'pip-format',
          'PIP must be a single amount (e.g., 10K)',
          function (value) {
            return validateCoverageLimitYup(value || '', 'PIP');
          }
        ),
      reasonText: Yup.string().required('Required'),
      limits: Yup.string().when(['biLimit', 'cslLimit', 'pipLimit'], {
        is: (biLimit, cslLimit, pipLimit) => !biLimit && !cslLimit && !pipLimit,
        then: Yup.string().required('One is required'),
        otherwise: Yup.string(),
      }),
    }),
  });
};

const PriorPolicyForm = ({
  canChallenge,
  priorPolicy,
  commands,
  driverId,
  onCancelAdd,
}: PriorPolicyFormPropsType) => {
  const identifier = `${priorPolicy.policyNumber}-prior-policy`;
  const { appId, fetchRate, rate, setDetails, config } = useAppContext();
  const { toast } = useToast();
  const [messages, setMessages] = useState<Array<ReportChangeMessageType>>([]);
  const { editingId, changeEditingId } = useEdit();

  const challengeCommand = useMemo(() => {
    return commands?.find(
      (command: { name: string }) => command.name === 'challenge_prior_policy'
    );
  }, [commands]);

  const [, challengePriorPolicyData] = useLazyQuoteApi<GetDetailsResponseType>({
    method: 'POST',
    url: `/insurance_applications/${appId}/insurable_parties/${driverId}/prior_policies/${priorPolicy.policyNumber}/challenge`,
  });

  const handleSubmit = async (values: PriorPolicyChallengeFormValuesType) => {
    try {
      const response = await challengePriorPolicyData({
        data:
          values[identifier].challengeType === 'edit'
            ? { ...values[identifier], challengeReason: 'edit_with_proof' }
            : values[identifier],
        transformRequest: transformChallengePriorPolicyRequest,
        transformResponse: transformAppDetails,
      });

      if (response.data.status === 'open') {
        const newRate = await fetchRate();
        rate &&
          newRate &&
          setMessages(
            getReportMessages(
              rate,
              newRate,
              'disputed report data',
              'accident-challenging'
            )
          );
      }
      changeEditingId(null);
      setDetails(response.data);
    } catch (e) {
      toast({
        type: 'error',
        message: getErrorMessage(e),
        withSecondaryNav: false,
        icon: 'error',
      });
    }
  };

  return (
    <Formik<PriorPolicyChallengeFormValuesType>
      initialValues={getChallengePriorPolicyInitialFormValues(
        priorPolicy,
        identifier
      )}
      validationSchema={getValidationSchema(identifier)}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ values, setFieldValue, errors }) => {
        const isEditing =
          editingId === identifier &&
          values[identifier].challengeType === 'edit';

        return (
          <>
            <ReportDetailRow
              identifier={identifier}
              canChallenge={canChallenge}
              challengeCommand={challengeCommand}
              source={priorPolicy.source}
              type="prior-policies"
              shortSource
              checkmarked={null}
              onEdit={() => {
                setFieldValue(`${identifier}.challengeType`, 'edit');
              }}
              challengeForm={
                <ChallengeForm
                  variant={values[identifier].challengeType || 'edit'}
                  identifier={identifier}
                  type="prior-policies"
                  includeRemove
                  challengeReasonConfig={
                    config?.drivers.accidents.challengeReasonOptions || {}
                  }
                  onRemove={() => {
                    setFieldValue(`${identifier}.challengeType`, 'remove');
                  }}
                  onCancelAdd={onCancelAdd}
                />
              }
            >
              <PriorPolicyChallengeForm
                identifier={identifier}
                values={values}
                setFieldValue={setFieldValue}
                isEditing={isEditing}
                errors={errors}
                priorPolicy={priorPolicy}
              />
            </ReportDetailRow>
            <ReportMessages messages={messages} className={styles.message} />
          </>
        );
      }}
    </Formik>
  );
};

export default PriorPolicyForm;
